import type { BookingCommandResponse } from './bookings';
import { requestJson } from './client';

// --- Types (order BookingController owner endpoints / VendorStatsController) ---

export type StudioBookingListItem = {
  bookingId: string;
  roomId: string;
  studioId: string;
  startsAt: string;
  endsAt: string;
  status: string;
  totalPrice: number;
};

/** Spring Data Page envelope. */
export type SpringPage<T> = {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
  empty: boolean;
};

export type StudioBookingsQuery = {
  page?: number;
  size?: number;
  status?: string[];
  from?: string;
  to?: string;
  q?: string;
  roomId?: string;
};

export type VendorStatsBucket = {
  key: string;
  revenueWon: number;
  bookingCount: number;
};

export type VendorStatsResponse = {
  fromKst: string;
  toKst: string;
  totalRevenueWon: number;
  totalBookingCount: number;
  approvedCount: number;
  pendingCount: number;
  rejectedCount: number;
  cancelledCount: number;
  uniqueCustomerCount: number;
  averageRevenuePerBooking: number;
  buckets: VendorStatsBucket[];
};

function generateNonce(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `nonce-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// --- Functions ---

export function approveBooking(bookingId: string) {
  return requestJson<BookingCommandResponse>(
    `/api/v1/bookings/${encodeURIComponent(bookingId)}/approve`,
    { headers: { 'X-Nonce': generateNonce() }, method: 'POST' },
  );
}

export function rejectBooking(bookingId: string) {
  return requestJson<BookingCommandResponse>(
    `/api/v1/bookings/${encodeURIComponent(bookingId)}/reject`,
    { headers: { 'X-Nonce': generateNonce() }, method: 'POST' },
  );
}

/** Vendor-side forced cancellation (full refund). */
export function cancelBookingAsVendor(bookingId: string, cancelReason: string) {
  return requestJson<BookingCommandResponse>(
    `/api/v1/vendors/me/bookings/${encodeURIComponent(bookingId)}/cancel`,
    {
      body: JSON.stringify({ cancelReason }),
      headers: { 'X-Nonce': generateNonce() },
      method: 'POST',
    },
  );
}

export function getStudioBookings(studioId: string, params: StudioBookingsQuery = {}) {
  const query = new URLSearchParams();
  if (params.page != null) query.set('page', String(params.page));
  if (params.size != null) query.set('size', String(params.size));
  (params.status ?? []).forEach((status) => query.append('status', status));
  if (params.from) query.set('from', params.from);
  if (params.to) query.set('to', params.to);
  if (params.q) query.set('q', params.q);
  if (params.roomId) query.set('roomId', params.roomId);
  const qs = query.toString();
  return requestJson<SpringPage<StudioBookingListItem>>(
    `/api/v1/bookings/studio/${encodeURIComponent(studioId)}${qs ? `?${qs}` : ''}`,
    { headers: { 'X-Nonce': generateNonce() }, method: 'GET' },
  );
}

export function getStudioStats(
  studioId: string,
  params: { from: string; to: string; groupBy?: string },
) {
  const query = new URLSearchParams({ from: params.from, to: params.to });
  if (params.groupBy) query.set('groupBy', params.groupBy);
  return requestJson<VendorStatsResponse>(
    `/api/v1/bookings/studio/${encodeURIComponent(studioId)}/stats?${query.toString()}`,
    { headers: { 'X-Nonce': generateNonce() }, method: 'GET' },
  );
}
